import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import {
  CircleStackIcon,
  DocumentIcon,
  DocumentPlusIcon,
  DocumentTextIcon,
  PaperClipIcon,
  PlusIcon,
  PuzzlePieceIcon,
  SparklesIcon,
  TableCellsIcon
} from '@heroicons/react/20/solid';

import { useFilePicker } from '~/components/cara-input/use-file-picker';
import { launchSmartFillExtension } from '~/components/cara-input/smart-fill-extension';

/**
 * Tailwind classes for the attachment picker menu panel. Mirrors the
 * MENU_PANEL_CLASS pattern in
 * `pkg/.../artifact-focus-html-document-render-button.tsx` so the animation
 * (scale + fade on the data-[closed]/data-[enter]/data-[leave] transitions)
 * is consistent with the Document Style picker the user already knows.
 *
 * Anchored `top end`, so we scale from `bottom-right` (the corner closest to
 * the trigger button) for a natural "emerges from button" feel.
 */
const ATTACHMENT_PICKER_MENU_CLASS = clsx(
  'z-[100] w-64 origin-bottom-right rounded-md bg-white p-1 shadow-lg ring-1 ring-black/5',
  'focus:outline-none',
  'transition data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0',
  'data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in',
  'dark:bg-neutral-900 dark:ring-white/10'
);

const MENU_HEADING_CLASS = clsx(
  'px-3 pt-2 pb-1 text-[11px] font-medium uppercase tracking-wide',
  'text-neutral-400 dark:text-neutral-500'
);

const MENU_SEPARATOR_CLASS = 'my-1 h-px bg-neutral-200 dark:bg-neutral-800';

const MENU_ITEM_CLASS = clsx(
  'group flex w-full items-center gap-2.5 rounded px-3 py-2 text-left text-sm',
  'text-neutral-700 dark:text-neutral-200',
  'data-[focus]:bg-neutral-100 data-[focus]:text-neutral-900',
  'dark:data-[focus]:bg-neutral-800 dark:data-[focus]:text-white',
  'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50'
);

const MENU_ITEM_ICON_CLASS = clsx(
  'size-4 shrink-0',
  'text-neutral-500 dark:text-neutral-400',
  'group-data-[focus]:text-neutral-700 dark:group-data-[focus]:text-neutral-200'
);

export type AttachmentPickerButtonProps = {
  disabled?: boolean;
  /**
   * Called with the user's selected files when "Attach files" is picked.
   * When omitted, the menu item is hidden — this lets callers opt-in to the
   * native upload flow without leaving a dead menu item behind.
   */
  onAttachFiles?: (files: File[]) => void;
  /** Forwarded to the hidden `<input type="file" />`. */
  accept?: string;
  /** Opens the AMS document browser. When omitted, the menu item is hidden. */
  onBrowseAms?: () => void;
  /**
   * Opens the "Fill a custom form" picker dialog. When omitted, the menu
   * item is hidden.
   */
  onPickCustomForm?: () => void;
  /** Opens the "Use a Word template" picker dialog. */
  onPickWordTemplate?: () => void;
  /** Opens the "Use an Excel template" picker dialog. */
  onPickExcelTemplate?: () => void;
  /** Opens the "Use a document template" (HTML) picker dialog. */
  onPickHtmlTemplate?: () => void;
  /** Current Cara workspace, used to launch Smart Fill from the menu. */
  smartFillWorkspaceId?: string;
};

/**
 * Trigger + menu for adding attachments to the Cara compose box. Each
 * attachment source (local files, custom forms, Word/HTML templates) is its
 * own concern; the button is essentially a router that exposes each source
 * as a menu item and delegates the actual work back to the parent through
 * typed callbacks.
 *
 * Sections (and their separator) are rendered conditionally so the menu
 * never shows dead items — if you don't wire up `onPickWordTemplate`, that
 * row simply isn't there.
 */
export const AttachmentPickerButton = (props: AttachmentPickerButtonProps) => {
  const filePicker = useFilePicker({
    onFiles: (files) => props.onAttachFiles?.(files),
    accept: props.accept,
    multiple: true
  });

  const hasFileSection = !!props.onAttachFiles;
  const hasAmsSection = !!props.onBrowseAms;
  const hasTemplateSection =
    !!props.onPickCustomForm ||
    !!props.onPickWordTemplate ||
    !!props.onPickExcelTemplate ||
    !!props.onPickHtmlTemplate;
  const hasSmartFillSection = !!props.smartFillWorkspaceId;

  return (
    <Headless.Menu as="div" className="relative inline-block">
      <Headless.MenuButton
        as="button"
        type="button"
        disabled={
          props.disabled ||
          (!hasFileSection && !hasAmsSection && !hasTemplateSection && !hasSmartFillSection)
        }
        aria-label="Add attachments"
        className="group text-neutral-600 dark:text-neutral-400 rounded-full p-1.5 hover:bg-neutral-100 dark:hover:bg-white/10 hover:text-neutral-900 dark:hover:text-white data-[disabled]:opacity-50 data-[disabled]:hover:bg-white dark:data-[disabled]:hover:bg-white/10 transition-all duration-150"
      >
        <PlusIcon className="size-5 group-hover:text-neutral-900 dark:group-hover:text-white transition-transform duration-200 group-data-[open]:rotate-90" />
      </Headless.MenuButton>

      {/* Hidden file input lives next to the trigger so `useFilePicker` can
          imperatively open it without the parent needing to know it exists. */}
      <input {...filePicker.inputProps} />

      <AttachmentPickerMenu
        onAttachFiles={hasFileSection ? () => filePicker.open() : undefined}
        onBrowseAms={props.onBrowseAms}
        onPickCustomForm={props.onPickCustomForm}
        onPickWordTemplate={props.onPickWordTemplate}
        onPickExcelTemplate={props.onPickExcelTemplate}
        onPickHtmlTemplate={props.onPickHtmlTemplate}
        smartFillWorkspaceId={props.smartFillWorkspaceId}
      />
    </Headless.Menu>
  );
};

export const AttachmentPickerMenu = (props: {
  onAttachFiles?: () => void;
  onBrowseAms?: () => void;
  onPickCustomForm?: () => void;
  onPickWordTemplate?: () => void;
  onPickExcelTemplate?: () => void;
  onPickHtmlTemplate?: () => void;
  smartFillWorkspaceId?: string;
}) => {
  const hasFileSection = !!props.onAttachFiles;
  const hasAmsSection = !!props.onBrowseAms;
  const hasTemplateSection =
    !!props.onPickCustomForm ||
    !!props.onPickWordTemplate ||
    !!props.onPickExcelTemplate ||
    !!props.onPickHtmlTemplate;
  const smartFillWorkspaceId = props.smartFillWorkspaceId;

  return (
    <Headless.MenuItems
      transition
      anchor={{ to: 'top end', gap: 8 }}
      className={ATTACHMENT_PICKER_MENU_CLASS}
    >
      {hasFileSection && (
        <Headless.MenuSection>
          <Headless.MenuHeading className={MENU_HEADING_CLASS}>
            From your device
          </Headless.MenuHeading>
          <Headless.MenuItem>
            <button type="button" className={MENU_ITEM_CLASS} onClick={props.onAttachFiles}>
              <PaperClipIcon className={MENU_ITEM_ICON_CLASS} />
              <span>Attach files</span>
            </button>
          </Headless.MenuItem>
        </Headless.MenuSection>
      )}

      {hasFileSection && hasAmsSection && (
        <Headless.MenuSeparator className={MENU_SEPARATOR_CLASS} />
      )}

      {hasAmsSection && (
        <Headless.MenuSection>
          <Headless.MenuHeading className={MENU_HEADING_CLASS}>From your AMS</Headless.MenuHeading>
          <Headless.MenuItem>
            <button type="button" className={MENU_ITEM_CLASS} onClick={props.onBrowseAms}>
              <CircleStackIcon className={MENU_ITEM_ICON_CLASS} />
              <span>Browse AMS</span>
            </button>
          </Headless.MenuItem>
        </Headless.MenuSection>
      )}

      {(hasFileSection || hasAmsSection) && hasTemplateSection && (
        <Headless.MenuSeparator className={MENU_SEPARATOR_CLASS} />
      )}

      {hasTemplateSection && (
        <Headless.MenuSection>
          <Headless.MenuHeading className={MENU_HEADING_CLASS}>Templates</Headless.MenuHeading>
          {props.onPickCustomForm && (
            <Headless.MenuItem>
              <button type="button" className={MENU_ITEM_CLASS} onClick={props.onPickCustomForm}>
                <DocumentPlusIcon className={MENU_ITEM_ICON_CLASS} />
                <span>Fill a custom form</span>
              </button>
            </Headless.MenuItem>
          )}
          {props.onPickWordTemplate && (
            <Headless.MenuItem>
              <button type="button" className={MENU_ITEM_CLASS} onClick={props.onPickWordTemplate}>
                <DocumentIcon className={MENU_ITEM_ICON_CLASS} />
                <span>Use a Word template</span>
              </button>
            </Headless.MenuItem>
          )}
          {props.onPickExcelTemplate && (
            <Headless.MenuItem>
              <button type="button" className={MENU_ITEM_CLASS} onClick={props.onPickExcelTemplate}>
                <TableCellsIcon className={MENU_ITEM_ICON_CLASS} />
                <span>Use an Excel template</span>
              </button>
            </Headless.MenuItem>
          )}
          {props.onPickHtmlTemplate && (
            <Headless.MenuItem>
              <button type="button" className={MENU_ITEM_CLASS} onClick={props.onPickHtmlTemplate}>
                <DocumentTextIcon className={MENU_ITEM_ICON_CLASS} />
                <span>Use a document template</span>
              </button>
            </Headless.MenuItem>
          )}
        </Headless.MenuSection>
      )}
      {smartFillWorkspaceId && (
        <>
          {(hasFileSection || hasAmsSection || hasTemplateSection) && (
            <Headless.MenuSeparator className={MENU_SEPARATOR_CLASS} />
          )}
          <Headless.MenuSection>
            <Headless.MenuHeading className={MENU_HEADING_CLASS}>
              Cara Extension
            </Headless.MenuHeading>
            <Headless.MenuItem>
              <button
                type="button"
                className={MENU_ITEM_CLASS}
                onClick={() => launchSmartFillExtension(smartFillWorkspaceId, 'GENERATE')}
              >
                <SparklesIcon className={MENU_ITEM_ICON_CLASS} />
                <span>Generate fill</span>
              </button>
            </Headless.MenuItem>
            <Headless.MenuItem>
              <button
                type="button"
                className={MENU_ITEM_CLASS}
                onClick={() => launchSmartFillExtension(smartFillWorkspaceId, 'OPEN')}
              >
                <PuzzlePieceIcon className={MENU_ITEM_ICON_CLASS} />
                <span>Open SmartFill</span>
              </button>
            </Headless.MenuItem>
          </Headless.MenuSection>
        </>
      )}
    </Headless.MenuItems>
  );
};
